/**
 * What could fill today's optional slots.
 *
 * The capacity ladder has granted optional units since it shipped — "Average"
 * is 3 core AND 1 optional — and the page never offered anything to put in
 * one. The first Stretch section read `in_progress_backlog`, which can only
 * show work already started; this is the server's richer pool: one next step
 * per active path, then habits not done today.
 */

export interface StretchCandidate {
  id: string
  type: 'task' | 'habit'
  title: string
  minutes: number | null
  /** Which path this row moves — 'goal' | 'skill' | 'habit'. */
  path_kind: string
  /** The path's own name, when it has one. */
  path_title: string | null
  /** One phrase saying why it is worth stretching for. */
  why: string
}

export interface Stretch {
  /** The ladder rung in force: recovery | gentle | average | momentum | superhero. */
  mode: string
  core_units: number
  optional_units: number
  candidates: StretchCandidate[]
}

const EMPTY: Stretch = { mode: 'average', core_units: 3, optional_units: 0, candidates: [] }

/** Never throws. An empty Stretch is a fine answer; a broken page is not. */
export async function getStretch(): Promise<Stretch> {
  try {
    const resp = await fetch('/api/orchestrator/stretch', { credentials: 'include' })
    if (!resp.ok) return EMPTY
    const body = (await resp.json()) as Partial<Stretch> & { success?: boolean }
    if (!body?.success) return EMPTY
    return {
      mode: body.mode ?? EMPTY.mode,
      core_units: Number(body.core_units ?? EMPTY.core_units),
      optional_units: Number(body.optional_units ?? 0),
      candidates: Array.isArray(body.candidates) ? body.candidates : [],
    }
  } catch {
    return EMPTY
  }
}


/**
 * Take a stretch item on: it joins today's plan and is pinned for the day.
 * Server-side this is one call that does both — pinning alone never reached
 * the rendered FocusPlan. Never throws; false means "did not happen".
 */
export async function takeOn(taskId: string): Promise<boolean> {
  try {
    const resp = await fetch('/api/orchestrator/take-on', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: Number(taskId) }),
    })
    if (!resp.ok) return false
    const body = (await resp.json()) as { success?: boolean }
    return !!body?.success
  } catch {
    return false
  }
}
